import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import {
	MIP_MANAGE_ROUTES,
	PROGRAMME_SETTINGS_ROUTES,
} from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { mipManagePath } from '@dealstack/frontend/app/pages/Routing/routes.utils.ts';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams.ts';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { toggleLegalDocument } from '@dealstack/frontend/shared/dataExchange/services/mip-manage.service.ts';
import { useManageableLegalDocuments } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/hooks/useManageableLegalDocuments.ts';

export const useManageLegalDocumentsDrawer = (opened: boolean) => {
	const tMipManage = useTranslationMipManage();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { dealId, mipId, investmentId } = useRequiredParams('dealId', 'mipId', 'investmentId');
	const { data, isLoading, isError } = useManageableLegalDocuments(opened);
	const [pendingIds, setPendingIds] = useState<string[]>([]);

	const manageableQueryKey = [
		API_PATHS.MIP_MIPID_INVESTOR_INVESTMENT_WORKFLOW_INVESTMENT_DETAILS_INVESTMENTID_LEGAL_DOCUMENTS_MANAGEABLE_GET,
		mipId,
		investmentId,
	];
	const legalDocumentsQueryKey = [
		API_PATHS.MIP_MIPID_INVESTOR_INVESTMENT_WORKFLOW_INVESTMENT_DETAILS_INVESTMENTID_LEGAL_DOCUMENTS_GET,
		mipId,
		investmentId,
	];

	const setPending = (templateId: string, on: boolean) =>
		setPendingIds((ids) => (on ? [...ids, templateId] : ids.filter((id) => id !== templateId)));

	const settle = (templateId: string) => {
		setPending(templateId, false);
		void queryClient.invalidateQueries({ queryKey: manageableQueryKey });
		void queryClient.invalidateQueries({ queryKey: legalDocumentsQueryKey });
	};

	const toggleMutation = useMutation({
		mutationFn: toggleLegalDocument,
		onError: () =>
			showToastV2({
				state: 'error',
				title: tMipManage('manage_legal_documents_drawer.toggle.error'),
			}),
	});

	const canMutateLegalDocuments = !!data?.canMutateLegalDocuments;
	const masterDocuments = data?.masterDocuments ?? [];

	const onToggle = (templateId: string, currentlyEnabled: boolean) => {
		if (!canMutateLegalDocuments || pendingIds.includes(templateId)) {
			return;
		}

		setPending(templateId, true);

		toggleMutation.mutate(
			{ mipId, investmentId, templateId, payload: { enabled: !currentlyEnabled } },
			{ onSettled: () => settle(templateId) }
		);
	};

	const goToMasterDocuments = () =>
		navigate({
			pathname: mipManagePath(
				`${MIP_MANAGE_ROUTES.SETTINGS}${PROGRAMME_SETTINGS_ROUTES.DOCUMENTS}`,
				{ dealId, mipId }
			),
		});

	return {
		masterDocuments,
		canMutateLegalDocuments,
		isLoading,
		isError,
		pendingIds,
		onToggle,
		goToMasterDocuments,
	};
};
